import { useMemo } from 'react';
import fixtures from '../data/fixtures.json';
import { evaluate, dividendYieldFraction, pctAboveLow } from '../rules/entryScreen';
import { rangePosition } from '../rules/portfolio';
import type { DemoStock, Thresholds, Verdict } from '../rules/types';

const stocks = fixtures.stocks as DemoStock[];
const watchTickers = fixtures.watchlist as string[];
const heldTickers = new Set(fixtures.positions.map((p) => p.ticker));

const pct = (f: number, d = 1) => `${(f * 100).toFixed(d)}%`;

/**
 * A condition chip states one bar of the entry screen and whether THIS stock clears it. The
 * numbers come from the live thresholds, never hardcoded, so moving a slider re-labels the
 * chips as well as re-deciding them — the point being that the rule is legible, not a verdict
 * handed down from nowhere.
 */
function Chip({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return <span className={`condChip ${ok ? 'ok' : 'no'}`}>{ok ? '✓' : '✕'} {children}</span>;
}

export default function Watchlist({
  thresholds: t, onOpenDeepDive,
}: {
  thresholds: Thresholds;
  onOpenDeepDive: (ticker: string) => void;
}) {
  const rows = useMemo(() => {
    return watchTickers
      .map((tk) => stocks.find((s) => s.ticker === tk))
      .filter((s): s is DemoStock => Boolean(s))
      .map((s) => {
        const y = dividendYieldFraction(s.annualDividendPerShare, s.price);
        const above = pctAboveLow(s.price, s.fiftyTwoWeekLow);
        const verdict = evaluate({
          pctAboveLowFraction: above,
          yieldFraction: y,
          enabled: t.entryScreenEnabled,
          nearLowThresholdFraction: t.nearLowFraction,
          minYieldFraction: t.minYieldFraction,
          maxYieldFraction: t.maxYieldFraction,
        });
        return {
          stock: s, yieldFraction: y, above, verdict,
          nearOK: above !== null && above >= 0 && above <= t.nearLowFraction,
          yieldOK: y !== null && y >= t.minYieldFraction,
          ceilingOK: y !== null && y <= t.maxYieldFraction,
          pos: rangePosition(s.price, s.fiftyTwoWeekLow, s.fiftyTwoWeekHigh),
        };
      });
  }, [t]);

  // Three groups, matching the Mac app: clean hits, flagged high-yield hits kept separate so
  // they're visible without being mistaken for green lights, and everything else.
  const groups: { key: Verdict; title: string; note: string }[] = [
    { key: 'qualifies', title: 'Ready', note: 'Clears every bar of the entry screen.' },
    { key: 'highYieldFlagged', title: 'High yield — verify sustainability',
      note: 'Meets the near-low and minimum-yield bars, but the yield is above your sanity ceiling. Surfaced deliberately rather than hidden, and deliberately not counted as Ready.' },
    { key: 'doesNotQualify', title: 'Not ready', note: 'At least one condition is unmet.' },
  ];

  return (
    <>
      {groups.map((g) => {
        const inGroup = rows.filter((r) => r.verdict === g.key);
        if (inGroup.length === 0) return null;
        return (
          <div className="group" key={g.key}>
            <h3 className={`groupTitle ${g.key}`}>{g.title} <span className="count">{inGroup.length}</span></h3>
            <p className="groupNote">{g.note}</p>

            <div className="rows">
              {inGroup.map((r) => (
                <button className="row watchRow" key={r.stock.ticker}
                        onClick={() => onOpenDeepDive(r.stock.ticker)}>
                  <div className="main">
                    <div className="name">
                      {r.stock.name}<span className="tk">{r.stock.ticker}</span>
                      {heldTickers.has(r.stock.ticker) && <span className="heldTag">held</span>}
                    </div>

                    <div className="chips">
                      <Chip ok={r.nearOK}>
                        within {pct(t.nearLowFraction, 0)} of low
                        {r.above !== null && <span className="chipVal"> ({pct(r.above, 0)})</span>}
                      </Chip>
                      <Chip ok={r.yieldOK}>
                        yield ≥ {pct(t.minYieldFraction, 1)}
                        <span className="chipVal"> ({r.yieldFraction ? pct(r.yieldFraction) : 'none'})</span>
                      </Chip>
                      <Chip ok={r.ceilingOK}>
                        within {pct(t.maxYieldFraction, 0)} ceiling
                      </Chip>
                    </div>

                    {/* The gauge shows WHERE in its year the price sits — a single glance
                        answer to "is this actually near the low?" that a percentage alone
                        doesn't give. */}
                    <div className="gauge" title={`${r.stock.fiftyTwoWeekLow.toFixed(2)} — ${r.stock.fiftyTwoWeekHigh.toFixed(2)}`}>
                      <div className="gaugeTrack">
                        <div className="gaugeMark" style={{ left: `${r.pos * 100}%` }} />
                      </div>
                      <div className="gaugeEnds">
                        <span>{r.stock.fiftyTwoWeekLow.toFixed(2)}</span>
                        <span>{r.stock.fiftyTwoWeekHigh.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="figs">
                    <div className="px">{r.stock.price.toFixed(2)}</div>
                    <div className="meta">{r.stock.sector}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {!t.entryScreenEnabled && (
        <p className="footnote">
          The entry screen is switched off, so nothing can qualify — every row falls to “Not
          ready” regardless of its numbers. Re-enable it in the rules panel above.
        </p>
      )}
    </>
  );
}
